/**
 * QuickSearchContext - Enables "rabbit hole" contextual searching
 * 
 * INLINE DRAWER APPROACH (not navigation):
 * - Shows results in a slide-out drawer WITHOUT leaving the page
 * - Uses fast cached lookups (no AI interpreter)
 * - Only navigates if user clicks "Open as Agent"
 * 
 * Examples:
 * - Click a company name → Drawer shows decision makers (instant, cached)
 * - Click a job title → Drawer shows candidates (fast lookup)
 * - Click a person → Drawer shows similar people (cached)
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearchPath } from './SearchPathContext';
import QuickSearchDrawer from '../components/shared/QuickSearchDrawer';

// =============================================================================
// CONTEXT
// =============================================================================
const QuickSearchContext = createContext(null);

export const useQuickSearch = () => {
  const context = useContext(QuickSearchContext);
  if (!context) {
    console.warn('useQuickSearch must be used within QuickSearchProvider');
    return { triggerQuickSearch: () => {} };
  }
  return context;
};

// =============================================================================
// MAP TYPE TO FAST LOOKUP ENDPOINT
// =============================================================================
const getQuickLookupConfig = (type, data) => {
  switch (type) {
    case 'company':
      // Show company information when clicking a company name
      // Pass through all available company data so drawer doesn't need to re-fetch
      return {
        endpoint: 'company-info',
        params: { 
          companyName: data.name,
          providedData: {
            name: data.name,
            industry: data.industry,
            location: data.location,
            employeeCount: data.employeeCount,
            revenue: data.revenue,
            website: data.website,
            logo: data.logo,
            description: data.description,
            foundedYear: data.foundedYear,
            linkedinUrl: data.linkedinUrl,
          }
        },
        title: data.name,
        fullSearchQuery: `Decision makers at ${data.name}`,
        fullSearchMode: 'people',
        isCompanyInfo: true, // Flag for special rendering
      };
    
    case 'decision-makers-direct':
      // Direct decision makers search (from company info view)
      return {
        endpoint: 'decision-makers',
        params: { companyName: data.name, companyId: data.companyId, limit: 10 },
        title: `Decision Makers at ${data.name}`,
        fullSearchQuery: `Decision makers at ${data.name}`,
        fullSearchMode: 'people',
      };
    
    case 'people-at-company-direct':
      // Direct people search (from company info view)
      return {
        endpoint: 'people-at-company',
        params: { companyName: data.name, limit: 10 },
        title: `People at ${data.name}`,
        fullSearchQuery: `People at ${data.name}`,
        fullSearchMode: 'people',
      };
      
    case 'person':
      // If we have company info, find similar people at the company
      if (data.company) {
        return {
          endpoint: 'people-at-company',
          params: { companyName: data.company, limit: 10 },
          title: `People at ${data.company}`,
          fullSearchQuery: `People at ${data.company}`,
          fullSearchMode: 'people',
        };
      }
      // Otherwise find similar by title
      return {
        endpoint: 'similar-people',
        params: { jobTitle: data.title, industry: data.industry, limit: 10 },
        title: `Similar to ${data.name}`,
        fullSearchQuery: `${data.title} ${data.industry || ''}`,
        fullSearchMode: 'people',
      };
      
    case 'job':
      // Find candidates for this role
      return {
        endpoint: 'similar-people',
        params: { jobTitle: data.title, limit: 10 },
        title: `Candidates for ${data.title}`,
        fullSearchQuery: `${data.title} candidates`,
        fullSearchMode: 'people',
      };
      
    case 'news':
    case 'signal': {
      // Find decision makers at the company mentioned
      // Try multiple fields for company name - SERP signals can have it in different places
      const signalCompany = data.company || data.companyName || data.name || 
        (typeof data.source === 'string' ? data.source : data.source?.name) || null;
      
      if (signalCompany) {
        return {
          endpoint: 'decision-makers',
          params: { companyName: signalCompany, limit: 10 },
          title: `Contacts at ${signalCompany}`,
          fullSearchQuery: `Decision makers at ${signalCompany}`,
          fullSearchMode: 'people',
        };
      }
      // Fallback: if we have a topic/headline, search by that
      if (data.topic || data.headline || data.title) {
        const topic = data.topic || data.headline || data.title;
        return {
          endpoint: 'similar-people',
          params: { jobTitle: topic, industry: data.industry, limit: 10 },
          title: `Experts: ${topic.substring(0, 30)}...`,
          fullSearchQuery: topic,
          fullSearchMode: 'people',
        };
      }
      console.warn('QuickSearch: No company or topic found in signal data:', data);
      return null;
    }
      
    case 'skill':
      return {
        endpoint: 'similar-people',
        params: { jobTitle: data.name, limit: 10 },
        title: `${data.name} Experts`,
        fullSearchQuery: `${data.name} expert`,
        fullSearchMode: 'people',
      };
      
    case 'industry':
      return {
        endpoint: 'similar-people',
        params: { industry: data.name, limit: 10 },
        title: `${data.name} Leaders`,
        fullSearchQuery: `${data.name} executives`,
        fullSearchMode: 'people',
      };
      
    default:
      return null;
  }
};

// =============================================================================
// PROVIDER COMPONENT
// =============================================================================
export function QuickSearchProvider({ children }) {
  const navigate = useNavigate();
  const { addStep } = useSearchPath();
  
  // Drawer state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerConfig, setDrawerConfig] = useState(null);
  
  // Trigger quick search - Opens INLINE DRAWER (doesn't navigate!)
  const triggerQuickSearch = useCallback((type, data, _event) => {
    console.log('⚡ Quick search triggered:', type, data?.name || data?.title);
    
    const config = getQuickLookupConfig(type, data);
    
    if (!config) {
      console.warn('No quick lookup config for type:', type);
      return;
    }
    
    // Open the drawer with this config
    setDrawerConfig({
      searchType: config.endpoint,
      searchParams: config.params,
      title: config.title,
      fullSearchQuery: config.fullSearchQuery,
      fullSearchMode: config.fullSearchMode,
      sourceType: type,
      sourceData: data,
    });
    setIsDrawerOpen(true);
    
  }, []);
  
  // Handle "Open as Agent" - this navigates to full search
  const handleOpenAsAgent = useCallback((config) => {
    if (!config) return;
    
    // Add to search path
    addStep({
      type: 'quick_search_expanded',
      query: drawerConfig.fullSearchQuery,
      mode: drawerConfig.fullSearchMode,
      source: {
        type: drawerConfig.sourceType,
        name: drawerConfig.sourceData?.name || drawerConfig.sourceData?.title,
        data: drawerConfig.sourceData,
      },
      results: 0,
    });
    
    // NOW navigate to full search
    navigate('/app', {
      state: {
        query: drawerConfig.fullSearchQuery,
        mode: drawerConfig.fullSearchMode,
        source: 'quick_search',
        sourceType: drawerConfig.sourceType,
        sourceName: drawerConfig.sourceData?.name,
        autoExecute: true,
      }
    });
    
    // Dispatch event for immediate search execution
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('eq:quick-search', { 
        detail: { 
          query: drawerConfig.fullSearchQuery, 
          mode: drawerConfig.fullSearchMode,
          autoExecute: true,
          sourceType: drawerConfig.sourceType,
          sourceName: drawerConfig.sourceData?.name,
        } 
      }));
    }, 100);
    
  }, [navigate, addStep, drawerConfig]);
  
  const closeDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setDrawerConfig(null);
  }, []);
  
  return (
    <QuickSearchContext.Provider value={{ triggerQuickSearch }}>
      {children}
      
      {/* Global Quick Search Drawer */}
      <QuickSearchDrawer
        isOpen={isDrawerOpen}
        onClose={closeDrawer}
        searchType={drawerConfig?.searchType}
        searchParams={drawerConfig?.searchParams}
        title={drawerConfig?.title}
        onOpenAsAgent={() => handleOpenAsAgent(drawerConfig)}
      />
    </QuickSearchContext.Provider>
  );
}

export default QuickSearchContext;
